import { useEffect, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { RoleGuard } from '../../components/auth/RoleGuard';
import { UserFormWizard, UserLocationBadges } from '@/components/settings';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '../../lib/api';
import { isImpersonating, startImpersonationSession } from '@/lib/impersonation';
import { notify } from '../../lib/notify';

interface UserLocation {
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  warehouseType: string;
}

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roles: { id: string; name: string }[];
  locationAccessUnrestricted?: boolean;
  locations?: UserLocation[];
}

interface Role {
  id: string;
  name: string;
}

export default function UsersSettings() {
  const { user: currentUser, roles: authRoles, hasPermission } = useAuth();
  const canImpersonate =
    authRoles.includes('platformAdmin') && !isImpersonating();

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState<'create' | 'edit'>('create');
  const [wizardUser, setWizardUser] = useState<User | null>(null);

  const [impersonateUser, setImpersonateUser] = useState<User | null>(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(search && { q: search }),
        ...(statusFilter !== 'all' && { isActive: statusFilter }),
        ...(roleFilter !== 'all' && { roleId: roleFilter }),
      });

      const res = await apiFetch(`/api/admin/users?${params}`);
      if (res.status === 403) {
        setUsers([]);
        setTotal(0);
        return;
      }
      const data = await res.json();
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setUsers([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, statusFilter, roleFilter]);

  async function loadRoles() {
    try {
      const res = await apiFetch('/api/v1/roles/dropdown');
      if (!res.ok) return;
      const data = await res.json();
      setRoles(data);
    } catch {
      setRoles([]);
    }
  }

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function toggleUserStatus(user: User) {
    try {
      await apiFetch(`/api/admin/users/${user.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !user.isActive }),
      });
      loadUsers();
    } catch {
      notify.error('Failed to update user status');
    }
  }

  async function sendPasswordReset(user: User) {
    try {
      await apiFetch(`/api/admin/users/${user.id}/reset`, { method: 'POST' });
      notify.success('Password reset email sent');
    } catch {
      notify.error('Failed to send password reset');
    }
  }

  function openCreateWizard() {
    setWizardMode('create');
    setWizardUser(null);
    setWizardOpen(true);
  }

  function openEditWizard(user: User) {
    setWizardMode('edit');
    setWizardUser(user);
    setWizardOpen(true);
  }

  function openImpersonateDialog(user: User) {
    setImpersonateUser(user);
    setImpersonateReason('');
  }

  async function handleImpersonate() {
    if (!impersonateUser || !impersonateReason.trim()) return;
    if (!currentUser?.organizationId) {
      notify.error('Your organization could not be determined');
      return;
    }

    setImpersonating(true);
    try {
      const res = await apiFetch('/api/v1/platform/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetOrganizationId: currentUser.organizationId,
          targetUserId: impersonateUser.id,
          reason: impersonateReason.trim(),
        }),
      });

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        const message =
          data &&
          typeof data === 'object' &&
          'error' in data &&
          typeof (data as { error: unknown }).error === 'string'
            ? (data as { error: string }).error
            : 'Failed to impersonate';
        notify.error(message);
        return;
      }

      const result = await res.json();
      startImpersonationSession(result);
      window.location.href = '/';
    } catch {
      notify.error('Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  }

  const canManageUsers = hasPermission('users.write');
  const impersonateTargetName = impersonateUser
    ? [impersonateUser.firstName, impersonateUser.lastName].filter(Boolean).join(' ') ||
      impersonateUser.email
    : '';

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Users</h1>
          <RoleGuard permission="users.write">
            <Button onClick={openCreateWizard}>Add User</Button>
          </RoleGuard>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <Input
                placeholder="Search users..."
                value={search}
                onChange={e => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="max-w-sm"
              />
              <Select value={roleFilter} onValueChange={v => { setPage(1); setRoleFilter(v); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={v => { setPage(1); setStatusFilter(v); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="hidden sm:table-cell">Role</TableHead>
                  <TableHead className="hidden md:table-cell">Locations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Last Login</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{user.email}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {user.roles.map(r => (
                          <Badge key={r.id} variant="secondary" className="mr-1">
                            {r.name}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {user.locationAccessUnrestricted ? (
                          <span className="text-sm text-muted-foreground">All locations</span>
                        ) : !user.locations || user.locations.length === 0 ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : (
                          <UserLocationBadges locations={user.locations} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isActive ? 'default' : 'destructive'}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground lg:table-cell">
                        {formatDate(user.lastLoginAt)}
                      </TableCell>
                      <TableCell>
                        {(canManageUsers ||
                          (canImpersonate &&
                            user.id !== currentUser?.id &&
                            user.isActive)) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <RoleGuard permission="users.write" fallback={null}>
                                <DropdownMenuItem onClick={() => openEditWizard(user)}>
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendPasswordReset(user)}>
                                  Reset Password
                                </DropdownMenuItem>
                              </RoleGuard>
                              {canImpersonate &&
                                user.id !== currentUser?.id &&
                                user.isActive && (
                                  <DropdownMenuItem
                                    onClick={() => openImpersonateDialog(user)}
                                  >
                                    Impersonate
                                  </DropdownMenuItem>
                                )}
                              <RoleGuard permission="users.write" fallback={null}>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => toggleUserStatus(user)}
                                  className={user.isActive ? 'text-destructive' : ''}
                                >
                                  {user.isActive ? 'Deactivate' : 'Activate'}
                                </DropdownMenuItem>
                              </RoleGuard>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
                <Select
                  value={pageSize.toString()}
                  onValueChange={v => {
                    setPage(1);
                    setPageSize(parseInt(v, 10));
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <UserFormWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          mode={wizardMode}
          roles={roles}
          user={wizardUser}
          onSuccess={loadUsers}
        />

        <Dialog
          open={impersonateUser !== null}
          onOpenChange={(open) => {
            if (!open) {
              setImpersonateUser(null);
              setImpersonateReason('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Impersonate user</DialogTitle>
              <DialogDescription>
                Start a read-only session as {impersonateTargetName}. Enter a reason for the
                audit log.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1 text-sm">
                <p className="text-muted-foreground">User</p>
                <p className="font-medium">{impersonateTargetName}</p>
                <p className="text-muted-foreground">{impersonateUser?.email}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="impersonate-reason">Reason *</Label>
                <Input
                  id="impersonate-reason"
                  value={impersonateReason}
                  onChange={(e) => setImpersonateReason(e.target.value)}
                  placeholder="Support ticket #12345"
                  disabled={impersonating}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Session starts read-only. Use the banner to enable writes or stop impersonating.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setImpersonateUser(null);
                  setImpersonateReason('');
                }}
                disabled={impersonating}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleImpersonate()}
                disabled={!impersonateReason.trim() || impersonating}
              >
                {impersonating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start Impersonation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
